import type { ReactNode } from "react";

export type IconName =
  | "bulb" | "gear" | "building" | "cube" | "coins" | "trend-bars" | "cart" | "people" | "person"
  | "target" | "doc" | "tax" | "columns" | "portal" | "database" | "workflow" | "accelerator"
  | "search" | "cloud" | "bars" | "trend-up" | "clock" | "shield" | "bolt" | "play";

const paths: Record<IconName, ReactNode> = {
  bulb: <><path d="M18 4a9 9 0 00-5 16.5V25h10v-4.5A9 9 0 0018 4z" /><path d="M14 29h8M15 32h6" /></>,
  gear: <><circle cx="18" cy="18" r="5" /><path d="M18 4v4M18 28v4M4 18h4M28 18h4M8 8l3 3M25 25l3 3M28 8l-3 3M11 25l-3 3" /></>,
  building: <path d="M6 32V16h8v16M14 32V6h10v26M24 32V14h6v18M3 32h30M18 12h2M18 17h2M18 22h2" />,
  cube: <><path d="M18 4l13 7v14l-13 7-13-7V11z" /><path d="M5 11l13 7 13-7M18 18v14" /></>,
  coins: <path d="M8 9c0-2.2 4.5-4 10-4s10 1.8 10 4-4.5 4-10 4S8 11.2 8 9zM8 9v6c0 2.2 4.5 4 10 4s10-1.8 10-4V9M8 15v6c0 2.2 4.5 4 10 4s10-1.8 10-4v-6M8 21v6c0 2.2 4.5 4 10 4s10-1.8 10-4v-6" />,
  "trend-bars": <><path d="M4 32h28M8 27v-7M15 27V15M22 27v-5M29 27V10" /><path d="M5 12l8-6 6 5 11-8" /></>,
  cart: <><path d="M3 6h5l3 16h17l3-12H10" /><circle cx="14" cy="29" r="2" /><circle cx="26" cy="29" r="2" /><path d="M16 12l3 3 5-5" /></>,
  people: <><circle cx="18" cy="9" r="4" /><circle cx="7" cy="14" r="3" /><circle cx="29" cy="14" r="3" /><path d="M10 31v-5a8 8 0 0116 0v5M2 26v-3a5 5 0 016-4M34 26v-3a5 5 0 00-6-4" /></>,
  person: <><circle cx="18" cy="11" r="6" /><path d="M6 32c0-7 5-11 12-11s12 4 12 11" /></>,
  target: <><circle cx="16" cy="20" r="12" /><circle cx="16" cy="20" r="7" /><circle cx="16" cy="20" r="2" /><path d="M18 18L31 5M26 5h5v5" /></>,
  doc: <><path d="M9 3h13l7 7v23H9z" /><path d="M22 3v7h7M14 17h10M14 22h10M14 27h6" /></>,
  tax: <><path d="M9 3h13l7 7v23H9z" /><path d="M22 3v7h7M14 27l10-10" /><circle cx="15" cy="19" r="1.6" /><circle cx="23" cy="26" r="1.6" /></>,
  columns: <><rect x="5" y="17" width="6" height="15" /><rect x="15" y="8" width="6" height="24" /><rect x="25" y="21" width="6" height="11" /></>,
  portal: <><rect x="3" y="5" width="30" height="21" rx="2" /><path d="M12 32h12M18 26v6" /><circle cx="18" cy="13" r="3" /><path d="M12 22c1-4 11-4 12 0" /></>,
  database: <path d="M8 9c0-2.2 4.5-4 10-4s10 1.8 10 4-4.5 4-10 4S8 11.2 8 9zM8 9v18c0 2.2 4.5 4 10 4s10-1.8 10-4V9M8 18c0 2.2 4.5 4 10 4s10-1.8 10-4" />,
  workflow: <><circle cx="9" cy="9" r="4" /><circle cx="9" cy="27" r="4" /><circle cx="27" cy="27" r="4" /><path d="M9 13v10M13 27h10M13 9h6a4 4 0 014 4v10" /></>,
  accelerator: <><path d="M18 3l13 7v14l-13 7-13-7V10z" /><path d="M12 18l4 4 8-8" /></>,
  search: <><circle cx="15" cy="15" r="10" /><path d="M23 23l9 9" /></>,
  cloud: <path d="M10 29a7 7 0 01-1-14 9 9 0 0117-2 6 6 0 01-1 16z" />,
  bars: <path d="M6 31V21M14 31V13M22 31V8M30 31V17" strokeWidth={3.2} />,
  "trend-up": <><path d="M3 28l9-9 6 6 14-15" /><path d="M24 10h8v8" /></>,
  clock: <><circle cx="18" cy="18" r="14" /><path d="M18 9v9l6 4" /></>,
  shield: <><path d="M18 3l12 5v9c0 8-5 13-12 16C11 30 6 25 6 17V8z" /><path d="M12 18l4 4 8-8" /></>,
  bolt: <path d="M20 3L7 20h10l-2 13 14-18H19z" />,
  play: <><circle cx="18" cy="18" r="14" /><path d="M14 11l11 7-11 7z" fill="currentColor" /></>,
};

export default function Icon({ name, size = 34, className }: { name: IconName; size?: number; className?: string }) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 36 36"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      {paths[name]}
    </svg>
  );
}
